import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusPill } from "@/components/StatusPill";
import { Calendar, FileText, FolderOpen, ArrowRight, Download, Clock, BarChart3, Map as MapIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";

export default function PortalDashboard() {
    const { user } = useAuth();
    const { branchParam, selectedBranch, hasBranches } = useBranch();
    const [next, setNext] = useState(null);
    const [last, setLast] = useState(null);
    const [docsCount, setDocsCount] = useState(0);
    const today = format(new Date(), "yyyy-MM-dd");

    useEffect(() => {
        (async () => {
            try {
                const [sched, reports, docs] = await Promise.all([
                    api.get("/schedules", { params: { status: "pending", ...branchParam } }),
                    api.get("/reports", { params: branchParam }),
                    api.get("/documents"),
                ]);
                const upcoming = sched.data
                    .filter((s) => s.scheduled_date >= today)
                    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.scheduled_time.localeCompare(b.scheduled_time));
                setNext(upcoming[0] || null);
                setLast(reports.data?.[0] || null);
                setDocsCount(docs.data.length);
            } catch {}
        })();
    }, [branchParam]);

    return (
        <div data-testid="portal-dashboard">
            <div className="mb-6">
                <h1 className="text-3xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Bienvenido, {user?.full_name?.split(" ")[0]}</h1>
                <p className="text-muted-foreground">
                    {hasBranches && selectedBranch
                        ? `Resumen de ${selectedBranch.name}`
                        : hasBranches
                        ? "Resumen de todas las sucursales"
                        : "Aquí está el resumen de tus servicios."}
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                {/* Next service */}
                <Card className="md:col-span-2 overflow-hidden">
                    <div className="bg-[hsl(var(--trulyn-navy))] text-white p-4 relative noise-overlay">
                        <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, hsla(48,100%,50%,0.18), transparent 60%)" }} />
                        <div className="text-xs uppercase tracking-widest text-white/70 mb-1">Próximo servicio</div>
                        {next ? (
                            <>
                                <div className="text-3xl font-semibold capitalize" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                                    {format(new Date(next.scheduled_date + "T00:00:00"), "EEEE d MMM", { locale: es })}
                                </div>
                                <div className="mt-2 flex items-center gap-3 text-sm">
                                    <span className="font-mono text-lg">{next.scheduled_time}</span>
                                    <span className="text-white/70">|</span>
                                    <span className="text-white/85">{next.invoice_type} • ${(next.service_value || 0).toFixed(2)}</span>
                                </div>
                                {next.instructions && (
                                    <p className="mt-3 text-xs text-white/70 line-clamp-2 max-w-xl">{next.instructions}</p>
                                )}
                            </>
                        ) : (
                            <div className="py-4 text-white/70">No hay servicios programados próximamente.</div>
                        )}
                    </div>
                </Card>

                {/* Documents count */}
                <Card className="p-4 flex flex-col justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Documentos</div>
                        <div className="text-4xl font-semibold font-mono">{docsCount}</div>
                        <p className="text-sm text-muted-foreground mt-1">Mapas y constancias</p>
                    </div>
                    <Link to="/portal/documents" data-testid="dashboard-docs-link">
                        <Button variant="outline" className="w-full mt-3">
                            <FolderOpen className="w-4 h-4 mr-1" /> Ver documentos
                        </Button>
                    </Link>
                </Card>

                {/* Last report */}
                <Card className="md:col-span-3 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Último reporte</h2>
                        <Link to="/portal/reports"><Button variant="ghost" size="sm">Ver todos <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                    </div>
                    {last ? (
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="font-mono text-sm text-muted-foreground">{last.service_date}</div>
                                <div className="font-medium mt-1">Factura {last.dte_invoice_number}</div>
                                <Badge variant="outline" className="mt-1">Recibido por {last.accepted_by_name || "—"}</Badge>
                            </div>
                            <div className="flex gap-2">
                                <Link to={`/portal/reports/${last.id}`}><Button variant="outline">Ver detalle</Button></Link>
                                <a href={assetUrl(last.report_pdf_url)} target="_blank" rel="noreferrer"><Button className="bg-[hsl(var(--trulyn-navy))] text-white hover:bg-[hsl(var(--trulyn-navy))]/90"><Download className="w-4 h-4 mr-1" /> PDF</Button></a>
                            </div>
                        </div>
                    ) : <div className="text-muted-foreground text-sm py-3">Aún no hay reportes.</div>}
                </Card>

                {/* Insights & Maps quick access */}
                <Card className="md:col-span-3 p-4 bg-gradient-to-r from-indigo-50 to-amber-50 border-indigo-100"
                      data-testid="dashboard-insights-cta">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-indigo-700/80 font-semibold">Nuevo</div>
                            <h2 className="text-xl font-semibold mt-0.5" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                                Visualiza la actividad en tus instalaciones
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                Gráficas de capturas, consumo de cebos, insectos UV y mapas de calor con los puntos de mayor actividad histórica.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Link to="/portal/mapas" data-testid="dashboard-maps-link">
                                <Button className="bg-indigo-900 text-white hover:bg-indigo-800">
                                    <MapIcon className="w-4 h-4 mr-1.5" /> Ver mapas y estadísticas
                                </Button>
                            </Link>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
